import * as React from "react";
import { AlertTriangle, Ban, Bell, BellRing, Clock } from "lucide-react";
import { Modal } from "../../../ui/components/Modal";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import type { ProductAlertEpisode } from "../../lib/api/products";
import * as s from "./ActiveAlertDetailModal.css";

const SEVERITY_LABELS: Record<number, string> = {
  1: "Suggestion",
  2: "Low",
  3: "Medium",
  4: "High",
  5: "Critical",
};

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function useElapsedSince(startIso: string | null | undefined): string {
  const [display, setDisplay] = React.useState("—");
  React.useEffect(() => {
    if (!startIso) {
      setDisplay("—");
      return;
    }
    const update = () => {
      const elapsed = Date.now() - new Date(startIso).getTime();
      setDisplay(formatDuration(Math.max(0, elapsed)));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [startIso]);
  return display;
}

export type ActiveAlertDetailModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  alert: ProductAlertEpisode | null;
  /** True when the signed-in user currently receives notifications for this alert. */
  subscribed: boolean;
  /** False when subscription prefs could not be loaded / user can't manage them. */
  canManageSubscription?: boolean;
  subscriptionLoading?: boolean;
  onToggleSubscription?: () => Promise<void> | void;
  /** Plain-language explanation from Insights. Replaces the placeholder copy and widens the modal. */
  explanation?: React.ReactNode;
};

export function ActiveAlertDetailModal({
  open,
  setOpen,
  alert,
  subscribed,
  canManageSubscription = true,
  subscriptionLoading = false,
  onToggleSubscription,
  explanation,
}: ActiveAlertDetailModalProps) {
  const duration = useElapsedSince(alert?.openedAt);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setSaving(false);
      setError(null);
    }
  }, [open]);

  if (!alert) return null;

  const severity = Math.min(5, Math.max(1, alert.severity ?? 3));
  const severityLabel = SEVERITY_LABELS[severity] ?? `L${severity}`;
  const typeLabel = alert.type === "fault" ? "Fault" : "Alarm";
  const TypeIcon = alert.type === "fault" ? AlertTriangle : Bell;

  const handleToggle = async () => {
    if (!onToggleSubscription || saving || subscriptionLoading) return;
    setSaving(true);
    setError(null);
    try {
      await onToggleSubscription();
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "Failed to update alert notifications",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      minWidth="420px"
      maxWidth={explanation ? "780px" : "520px"}
      closeOnClickOff={!saving}
    >
      <Modal.Header>
        <div className={s.header}>
          <span className={s.iconWrap} data-severity={String(severity)} aria-hidden>
            <TypeIcon size={22} strokeWidth={2.25} />
          </span>
          <div className={s.headerText}>
            <h2 className={s.title}>{alert.name}</h2>
            <div className={s.metaRow}>
              <span className={s.code}>{alert.code}</span>
              <span className={s.combinedBadge} data-severity={String(severity)}>
                <TypeIcon size={11} strokeWidth={2.25} aria-hidden />
                {severityLabel} · {typeLabel}
              </span>
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className={s.body}>
          <span
            className={s.activeDurationBadge}
            title={`Opened ${alert.openedAt ? new Date(alert.openedAt).toLocaleString() : "—"}`}
          >
            <Clock size={13} aria-hidden />
            Active for {duration}
          </span>

          {alert.description ? (
            <p className={s.description}>{alert.description}</p>
          ) : explanation ? null : (
            <Text level="body-small" color="muted">
              More alert details will appear here in a future update.
            </Text>
          )}

          {explanation}

          {canManageSubscription ? (
            <div className={s.subscribeAction}>
              <p className={s.subscribeHint}>
                {subscribed
                  ? "You currently receive notifications for this alert."
                  : "You are not subscribed to notifications for this alert."}
              </p>
              <Button
                type="button"
                size="sm"
                color="neutral"
                variant="outline"
                iconLeft={
                  subscribed ? (
                    <Ban size={14} />
                  ) : (
                    <BellRing size={14} />
                  )
                }
                loading={saving || subscriptionLoading}
                disabled={saving || subscriptionLoading}
                onClick={() => void handleToggle()}
              >
                {subscribed
                  ? "Block my notifications for this alert"
                  : "Subscribe to notifications for this alert"}
              </Button>
              {error ? <p className={s.errorText}>{error}</p> : null}
            </div>
          ) : null}
        </div>
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: "flex-end" }}>
        <Button
          type="button"
          color="primary"
          onClick={() => setOpen(false)}
          disabled={saving}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
